import { Component, useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Link } from 'react-router-dom';
import { DataProvider, useData } from './data.js';
import { useRecords } from './ui.js';
import {
  setSecret, clearSecret, getSecret, getReadiness,
  whoAmI, logout, switchWorkspace,
} from './api.js';
import type { Identity } from './api.js';
import { Today } from './views/Today.js';
import { Welcome } from './views/Welcome.js';
import { SignIn } from './views/SignIn.js';
import { ChangePassword } from './views/ChangePassword.js';
import { Schedule } from './views/Schedule.js';
import { ResetPassword } from './views/ResetPassword.js';
import { Companies } from './views/Companies.js';
import { Record } from './views/Record.js';
import { Contacts } from './views/Contacts.js';
import { ContactRecord } from './views/ContactRecord.js';
import { Deals } from './views/Deals.js';
import { Research } from './views/Research.js';
import { Source } from './views/Source.js';
import { Build } from './views/Build.js';
import { Icp } from './views/Icp.js';
import { Reports } from './views/Reports.js';
import { SettingsView } from './views/SettingsView.js';
import { CreateMenu } from './views/CreateMenu.js';
import { Feedback } from './views/Feedback.js';


// SIX DESTINATIONS, FLAT. It was eleven, in three named groups — and four of
// the eleven ("ICP & personas", "Source accounts", "Research", "Build the
// list") were one job wearing four coats, because the Source wizard was BUILT
// to thread the other three and then all four stayed in the nav side by side.
// "Close" was a group whose only real member was a table with zero rows in it.
// Kyle: "clean up this UI and make it easy to navigate."
//
// The rule is the same one the account record and the fold stack already
// obey: a container that holds nothing becomes a line, and a screen reachable
// FROM its parent does not also need a nav slot. /icp, /research and /build
// all stay routed and linked from Source — the wizard that owns their order —
// they just stop competing with it. Groups went with them: six flat items
// need no headings, and a heading over two rows is chrome.
const NAV = [
  { to: '/', label: 'Today', end: true },
  // Where new accounts come from. One entry, not four — see above.
  { to: '/source', label: 'Source' },
  // The same word the rest of the app already uses for this screen: every
  // back-link on a record reads "← Pipeline".
  { to: '/companies', label: 'Pipeline' },
  { to: '/contacts', label: 'Contacts' },
  { to: '/schedule', label: 'Schedule' },
  { to: '/reports', label: 'Reports' },
] as const;

/**
 * "Deals" appears in the nav only once a deal exists. Zero deals across the
 * whole CRM today; a permanent slot for that is how a sidebar grows to eleven
 * entries. Renders nothing while loading — flashing an entry that then
 * vanishes reads as a bug.
 */
/**
 * THE FLOOR UNDER EVERY SCREEN. The interaction audit (2026-08-28) found a
 * render-time throw — new URL() on a scheme-less website — that unmounted the
 * entire app to a blank page, because nothing anywhere caught it. A view that
 * crashes now costs that view, not the application: the shell and nav stay,
 * the broken screen says so, and Reload recovers.
 *
 * (Re-applied 2026-08-30: the first patch script asserted this change in
 * memory and then wrote the UNPATCHED file back — the commit claimed a net
 * that was not in the build. Caught by grepping the deployed bundle for these
 * exact strings, which is the verification that should have run first.)
 */
class ViewBoundary extends Component<{ children: ReactNode }, { err: Error | null }> {
  state = { err: null as Error | null };
  static getDerivedStateFromError(err: Error) { return { err }; }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="crashed">
        <strong>This screen hit a bug.</strong>
        <span>{String(this.state.err.message ?? this.state.err)}</span>
        <button className="primary" onClick={() => location.reload()}>Reload</button>
      </div>
    );
  }
}

/**
 * THE NET UNDER EVERY FIRED-AND-FORGOTTEN MUTATION. More than a dozen buttons
 * failed as unhandled promise rejections — suppress, the stage select,
 * document deletes, mailbox actions — all invisible. A failed write is now at
 * worst a visible toast, never a lie of omission.
 */
function RejectionToast() {
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    const onReject = (e: PromiseRejectionEvent) => {
      const m = (e.reason as Error | undefined)?.message ?? String(e.reason ?? 'Unknown failure');
      setMsg(m.slice(0, 200));
    };
    window.addEventListener('unhandledrejection', onReject);
    return () => window.removeEventListener('unhandledrejection', onReject);
  }, []);
  if (!msg) return null;
  return (
    <div className="failtoast" role="alert">
      <span>Something failed: {msg}</span>
      <button className="iconbtn" aria-label="Dismiss" onClick={() => setMsg(null)}>×</button>
    </div>
  );
}

function DealsNavLink() {
  const deals = useRecords<{ id: string }>('deals');
  if (!deals.rows || deals.rows.length === 0) return null;
  return (
    <NavLink to="/deals">
      Deals<span className="count">{deals.rows.length}</span>
    </NavLink>
  );
}

function Shell() {
  const { live, companies, needsSecret, retry, error: bootError } = useData();
  const [creating, setCreating] = useState(false);
  // null = not yet known. Rendering the app before this resolves would flash
  // an empty CRM at someone who has not set anything up.
  const [onboarded, setOnboarded] = useState<boolean | null>(null);
  // Who is signed in, if it is a person rather than a workspace key.
  const [me, setMe] = useState<Identity | null>(null);

  useEffect(() => {
    if (needsSecret) return;
    getReadiness()
      .then((r) => setOnboarded(r.readiness.onboarded))
      // A failure here must not lock anyone out of their own CRM — assume set up.
      .catch(() => setOnboarded(true));

    // Only a session token has an identity behind it. A workspace key is not a
    // person, so this 401s and that is the correct answer, not an error.
    if (getSecret()?.startsWith('ses_')) {
      whoAmI().then(setMe).catch(() => setMe(null));
    } else {
      setMe(null);
    }
  }, [needsSecret]);

  // BEFORE the sign-in gate, deliberately. Somebody following a reset link
  // cannot sign in — that is why they have the link — so a screen behind the
  // gate is one they can never reach.
  if (window.location.pathname === '/reset') {
    return (
      <ResetPassword
        onIn={(id) => { setSecret(id.token); setMe(id); retry(); }}
      />
    );
  }

  if (needsSecret) {
    return (
      <SignIn
        onIn={(id) => { setSecret(id.token); setMe(id); retry(); }}
        onKey={(secret) => { setSecret(secret); retry(); }}
      />
    );
  }

  // ── A BORROWED PASSWORD GETS YOU EXACTLY ONE SCREEN ──────────────────────
  //
  // Above the router on purpose. Rendered here there is no nav to click past
  // and no URL to type around it, and because `must_change_password` rides on
  // every identity response — /api/auth/me included — reloading lands straight
  // back on it. A dismissible banner would be ignored by precisely the person
  // who needs to act on it.
  //
  // Only reachable for a PERSON. A workspace key is not somebody with a
  // password, so `me` is null and this never fires for a machine.
  if (me?.user.must_change_password) {
    return (
      <ChangePassword
        email={me.user.email}
        onDone={(token) => {
          // The old session was revoked server-side when the password changed,
          // so the new token has to be stored before anything else asks for
          // data — otherwise the next request 401s and dumps them at sign-in
          // having just successfully set a password.
          setSecret(token);
          whoAmI().then(setMe).catch(() => setMe(null));
          retry();
        }}
      />
    );
  }
  if (onboarded === false) {
    return <Welcome onDone={() => setOnboarded(true)} />;
  }

  const signOut = () => {
    // Revoke server-side FIRST. Clearing locally and skipping the call leaves a
    // live token in the sessions table, so "sign out" on a shared machine would
    // only hide the evidence.
    void logout().catch(() => {}).finally(() => { clearSecret(); location.reload(); });
  };

  // The API being down is a full-stop, not a footnote. Production never falls
  // back to fixtures any more (see data.tsx), so this is the state that
  // replaces the silent mannequin: what happened, and one button to try again.
  if (bootError && !companies) {
    return (
      <div className="crashed" style={{ margin: '18vh auto', maxWidth: 460 }}>
        <strong>Can't reach the server.</strong>
        <span>{bootError}</span>
        <button className="primary" onClick={retry}>Retry</button>
      </div>
    );
  }

  return (
    <div className="app">
      <nav className="side">
        <Link to="/" className="brand" aria-label="Hamer CRM — home">
          <img src="/logo.png" alt="Hamer" />
        </Link>

        <button className="createbtn" onClick={() => setCreating(true)}>
          <span aria-hidden="true">+</span> Create
        </button>

        {NAV.map((item) => (
          <NavLink key={item.to} to={item.to} end={'end' in item ? item.end : false}>
            {item.label}
            {item.to === '/companies' && companies && (
              <span className="count">{companies.length}</span>
            )}
          </NavLink>
        ))}
        {/* Deals EARNS its nav slot the day real money lands, exactly as its
            fold on the account record earns its box. Until then a top-level
            destination for an empty table is furniture. The /deals route
            itself stays live — nothing is torn out. */}
        <DealsNavLink />

        <div className="side-foot">
          <NavLink to="/settings">Settings</NavLink>

          {/* WHO YOU ARE AND WHICH WORKSPACE, at the bottom of the nav where
              every application puts it. Absent for a workspace-key session,
              because there is no "who" — and inventing one would be a lie the
              audit trail then repeats. */}
          {me && (
            <div className="whoami">
              <span className="whoami-name" title={me.user.email}>
                {me.user.name ?? me.user.email}
              </span>
              {me.workspaces.length > 1 || me.user.is_superadmin ? (
                <select
                  className="wspick"
                  value={me.current?.org_slug ?? ''}
                  onChange={(e) => void switchWorkspace(e.target.value)
                    .then(() => location.reload())}
                >
                  {me.current && !me.workspaces.some((w) => w.org_slug === me.current!.org_slug) && (
                    // A superadmin visiting somebody else's workspace. Named so
                    // it is obvious this is not one of theirs.
                    <option value={me.current.org_slug}>
                      {me.current.org_name} (visiting)
                    </option>
                  )}
                  {me.workspaces.map((w) => (
                    <option key={w.org_id} value={w.org_slug}>{w.org_name}</option>
                  ))}
                </select>
              ) : (
                <span className="meta">{me.current?.org_name ?? ''}</span>
              )}
              <button className="linkish" onClick={signOut}>Sign out</button>
            </div>
          )}

          <span className={live ? 'badge badge-live' : 'badge badge-fixture'}>
            {live ? 'live' : 'sample data'}
          </span>
          {!live && (
            <p className="sidenote">
              No database yet. Showing the real pipeline from db/seed.json. Run{' '}
              <code>npm run db:seed</code> once Neon is provisioned.
            </p>
          )}
        </div>
      </nav>

      <main className="content">
        <RejectionToast />
        <ViewBoundary>
        <Routes>
          <Route path="/" element={<Today />} />
          <Route path="/source" element={<Source />} />
          <Route path="/research" element={<Research />} />
          <Route path="/build" element={<Build />} />
          <Route path="/icp" element={<Icp />} />
          <Route path="/companies" element={<Companies />} />
          <Route path="/companies/:slug" element={<Record />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/contacts/:id" element={<ContactRecord />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/deals" element={<Deals />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/settings" element={<SettingsView />} />
          <Route path="*" element={<p className="empty">Not found.</p>} />
        </Routes>
        </ViewBoundary>
      </main>


      {creating && <CreateMenu onClose={() => setCreating(false)} />}

      {/* INSIDE the shell, so it only exists once somebody is signed in — there
          is no workspace to file against before that, and a feedback pill on a
          login screen collects nothing but confusion. Outside <main>, so it
          floats over every route rather than scrolling away with one. */}
      <Feedback />
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <DataProvider>
        <Shell />
      </DataProvider>
    </BrowserRouter>
  );
}
